import React, { useState, useEffect } from 'react';
import { Button } from './ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { 
  Plus, 
  Send, 
  Eye, 
  Edit,
  Trash2,
  Calculator,
  User,
  Calendar,
  MapPin,
  Package,
  Palette,
  Settings,
  DollarSign,
  Percent,
  Download
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';

const QuoteManagement = () => {
  const [quotes, setQuotes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [showCreateForm, setShowCreateForm] = useState(false);
  const [selectedQuote, setSelectedQuote] = useState(null);
  const [showQuoteDetails, setShowQuoteDetails] = useState(false);
  const { toast } = useToast();

  const [createForm, setCreateForm] = useState({
    user_email: '',
    customer_name: '',
    customer_postcode: '',
    windows: [
      {
        id: 1,
        label: 'Window 1',
        width: 1200,
        height: 1500,
        unit: 'mm',
        type: 'standard',
        shutter_colour: 'White',
        louvre_size: '63mm',
        control_type: 'Central Tilt Rod',
        hinge_colour: 'White',
        discount_percentage: 0
      }
    ],
    overall_discount_percentage: 0,
    notes: '',
    valid_until: ''
  });

  const windowTypes = [
    { value: 'standard', label: 'Standard Window - £260/m²' },
    { value: 'bay', label: 'Bay Window - £280/m²' },
    { value: 'shaped', label: 'Shaped Window - £360/m²' }
  ];

  const shutterColours = [
    'White', 'Cream', 'Light Grey', 'Dark Grey', 'Black', 
    'Natural Wood', 'Oak', 'Walnut', 'Mahogany'
  ];

  const louvreSizes = ['63mm', '76mm', '89mm', '114mm'];
  
  const controlTypes = ['Central Tilt Rod', 'Hidden Tilt Rod', 'Remote Control'];
  
  const hingeColours = ['White', 'Black', 'Brass', 'Chrome', 'Match Shutter'];

  useEffect(() => {
    fetchQuotes();
  }, []);

  const fetchQuotes = async () => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/quotes/all`);
      const data = await response.json();
      
      if (data.success) {
        setQuotes(data.data.quotes || []);
      } else {
        throw new Error(data.data?.error || 'Failed to fetch quotes');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to fetch quotes: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const calculateWindowCost = (window) => {
    // Calculate area in square meters
    let widthM, heightM;
    if (window.unit === 'mm') {
      widthM = window.width / 1000;
      heightM = window.height / 1000;
    } else {
      widthM = window.width / 100;
      heightM = window.height / 100;
    }
    
    const area = widthM * heightM;
    
    // Get price per square meter based on window type
    const pricePerSqm = window.type === 'standard' ? 260 : 
                       window.type === 'bay' ? 280 : 360;
    
    const baseCost = area * pricePerSqm;
    
    return {
      area: parseFloat(area.toFixed(2)),
      cost: parseFloat(baseCost.toFixed(2)),
      price_per_sqm: pricePerSqm
    };
  };

  const addWindow = () => {
    const newWindow = {
      id: createForm.windows.length + 1,
      label: `Window ${createForm.windows.length + 1}`,
      width: 1200,
      height: 1500,
      unit: 'mm',
      type: 'standard',
      shutter_colour: 'White',
      louvre_size: '63mm',
      control_type: 'Central Tilt Rod',
      hinge_colour: 'White',
      discount_percentage: 0
    };
    
    setCreateForm({
      ...createForm,
      windows: [...createForm.windows, newWindow]
    });
  };

  const removeWindow = (index) => {
    const updatedWindows = createForm.windows.filter((_, i) => i !== index);
    setCreateForm({
      ...createForm,
      windows: updatedWindows
    });
  };

  const updateWindow = (index, field, value) => {
    const updatedWindows = [...createForm.windows];
    updatedWindows[index] = { ...updatedWindows[index], [field]: value };
    setCreateForm({
      ...createForm,
      windows: updatedWindows
    });
  };

  const calculateQuoteTotal = () => {
    let subtotal = 0;
    let totalArea = 0;
    
    createForm.windows.forEach(window => {
      const calc = calculateWindowCost(window);
      const windowCost = calc.cost;
      const discountAmount = window.discount_percentage ? (windowCost * window.discount_percentage / 100) : 0;
      const discountedCost = windowCost - discountAmount;
      
      subtotal += discountedCost;
      totalArea += calc.area;
    });
    
    const overallDiscountAmount = createForm.overall_discount_percentage ? 
      (subtotal * createForm.overall_discount_percentage / 100) : 0;
    
    const total = subtotal - overallDiscountAmount;
    
    return {
      subtotal: subtotal.toFixed(2),
      totalArea: totalArea.toFixed(2),
      overallDiscountAmount: overallDiscountAmount.toFixed(2),
      total: total.toFixed(2)
    };
  };

  const createQuote = async () => {
    if (!createForm.user_email || !createForm.customer_name) {
      toast({
        variant: "destructive",
        title: "Error",
        description: "Please fill in customer email and name"
      });
      return;
    }

    setLoading(true);
    try {
      // Prepare windows with calculated values
      const preparedWindows = createForm.windows.map(window => {
        const calc = calculateWindowCost(window);
        return {
          ...window,
          area: calc.area,
          cost: calc.cost,
          price_per_sqm: calc.price_per_sqm
        };
      });

      const quoteData = {
        ...createForm,
        windows: preparedWindows,
        valid_until: createForm.valid_until ? new Date(createForm.valid_until).toISOString() : null
      };

      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/quotes/create`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(quoteData)
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Success",
          description: `Quote ${data.data.quote_number} created successfully`
        });
        setShowCreateForm(false);
        fetchQuotes();
        // Reset form
        setCreateForm({
          user_email: '',
          customer_name: '',
          customer_postcode: '',
          windows: [{
            id: 1,
            label: 'Window 1',
            width: 1200,
            height: 1500,
            unit: 'mm',
            type: 'standard',
            shutter_colour: 'White',
            louvre_size: '63mm',
            control_type: 'Central Tilt Rod',
            hinge_colour: 'White',
            discount_percentage: 0
          }],
          overall_discount_percentage: 0,
          notes: '',
          valid_until: ''
        });
      } else {
        throw new Error(data.data?.error || 'Failed to create quote');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to create quote: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const sendQuote = async (quoteId) => {
    setLoading(true);
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/quotes/send`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ quote_id: quoteId })
      });

      const data = await response.json();
      
      if (data.success) {
        toast({
          title: "Success",
          description: "Quote sent successfully"
        });
        fetchQuotes();
      } else {
        throw new Error(data.data?.error || 'Failed to send quote');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to send quote: ${error.message}`
      });
    } finally {
      setLoading(false);
    }
  };

  const downloadQuotePDF = async (quoteId, customerName, quoteNumber) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/quotes/download-pdf/${quoteId}`, {
        method: 'GET',
        headers: {
          'Accept': 'application/pdf',
        }
      });

      if (!response.ok) {
        throw new Error('Failed to download PDF');
      }

      // Create blob from response
      const blob = await response.blob();
      
      // Create download link
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      
      // Set filename from response header or create one
      const contentDisposition = response.headers.get('Content-Disposition');
      let filename = `Aston_Lily_Shutters_Quote_${quoteNumber || 'quote'}_${customerName || 'customer'}.pdf`;
      
      if (contentDisposition && contentDisposition.includes('filename=')) {
        const matches = contentDisposition.match(/filename="(.+)"/);
        if (matches && matches[1]) {
          filename = matches[1];
        }
      }
      
      link.setAttribute('download', filename);
      
      // Trigger download
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      // Clean up
      window.URL.revokeObjectURL(url);
      
      toast({
        title: "Success",
        description: "PDF quote downloaded successfully"
      });
      
    } catch (error) {
      console.error('Error downloading PDF:', error);
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to download PDF: ${error.message}`
      });
    }
  };

  const viewQuoteDetails = async (quoteId) => {
    try {
      const backendUrl = process.env.REACT_APP_BACKEND_URL || import.meta.env.REACT_APP_BACKEND_URL;
      const response = await fetch(`${backendUrl}/api/quotes/${quoteId}`);
      const data = await response.json();
      
      if (data.success) {
        setSelectedQuote(data.data.quote);
        setShowQuoteDetails(true);
      } else {
        throw new Error(data.data?.error || 'Failed to fetch quote details');
      }
    } catch (error) {
      toast({
        variant: "destructive",
        title: "Error",
        description: `Failed to fetch quote details: ${error.message}`
      });
    }
  };

  const getStatusBadgeColor = (status) => {
    switch (status) {
      case 'draft': return 'bg-gray-500';
      case 'sent': return 'bg-blue-500';
      case 'accepted': return 'bg-green-500';
      case 'rejected': return 'bg-red-500';
      case 'expired': return 'bg-orange-500';
      default: return 'bg-gray-500';
    }
  };

  const totals = calculateQuoteTotal();

  if (showQuoteDetails && selectedQuote) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-2xl font-bold">Quote Details</h2>
            <p className="text-gray-600">Quote #{selectedQuote.quote_number}</p>
          </div>
          <Button 
            variant="outline" 
            onClick={() => setShowQuoteDetails(false)}
          >
            Back to Quotes
          </Button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
          {/* Customer Info */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <User className="h-5 w-5" />
                Customer Information
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p><strong>Name:</strong> {selectedQuote.customer_name}</p>
                <p><strong>Email:</strong> {selectedQuote.user_email}</p>
                <p><strong>Postcode:</strong> {selectedQuote.customer_postcode}</p>
                <p><strong>Status:</strong> 
                  <Badge className={`ml-2 ${getStatusBadgeColor(selectedQuote.status)}`}>
                    {selectedQuote.status}
                  </Badge>
                </p>
              </div>
            </CardContent>
          </Card>

          {/* Quote Summary */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calculator className="h-5 w-5" />
                Quote Summary
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p><strong>Windows:</strong> {selectedQuote.windows_count}</p>
                <p><strong>Total Area:</strong> {selectedQuote.total_area}m²</p>
                <p><strong>Subtotal:</strong> £{selectedQuote.subtotal}</p>
                {selectedQuote.overall_discount_amount > 0 && (
                  <p><strong>Discount:</strong> -£{selectedQuote.overall_discount_amount}</p>
                )}
                <p className="text-lg font-bold"><strong>Total:</strong> £{selectedQuote.total_cost}</p>
              </div>
            </CardContent>
          </Card>

          {/* Quote Details */}
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Calendar className="h-5 w-5" />
                Quote Details
              </CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-2">
                <p><strong>Created:</strong> {new Date(selectedQuote.created_at).toLocaleDateString()}</p>
                <p><strong>Valid Until:</strong> {new Date(selectedQuote.valid_until).toLocaleDateString()}</p>
                <p><strong>Created By:</strong> {selectedQuote.created_by}</p>
                {selectedQuote.sent_at && (
                  <p><strong>Sent:</strong> {new Date(selectedQuote.sent_at).toLocaleDateString()}</p>
                )}
              </div>
            </CardContent>
          </Card>
        </div>

        {/* Windows Breakdown */}
        <Card>
          <CardHeader>
            <CardTitle>Windows Breakdown</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-4">
              {selectedQuote.windows.map((window, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
                    <div>
                      <h4 className="font-semibold">{window.label}</h4>
                      <p>{window.width}{window.unit} x {window.height}{window.unit}</p>
                      <p>{window.area}m² - {window.type}</p>
                    </div>
                    <div>
                      <p><strong>Colour:</strong> {window.shutter_colour}</p>
                      <p><strong>Louvre:</strong> {window.louvre_size}</p>
                    </div>
                    <div>
                      <p><strong>Control:</strong> {window.control_type}</p>
                      <p><strong>Hinge:</strong> {window.hinge_colour}</p>
                    </div>
                    <div className="text-right">
                      <p><strong>Cost:</strong> £{window.cost}</p>
                      {window.discount_percentage > 0 && (
                        <>
                          <p className="text-red-600">Discount: {window.discount_percentage}%</p>
                          <p><strong>Final:</strong> £{window.discounted_cost}</p>
                        </>
                      )}
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {selectedQuote.notes && (
          <Card>
            <CardHeader>
              <CardTitle>Notes</CardTitle>
            </CardHeader>
            <CardContent>
              <p>{selectedQuote.notes}</p>
            </CardContent>
          </Card>
        )}

        {/* Actions */}
        <div className="flex gap-4">
          <Button 
            variant="outline"
            onClick={() => downloadQuotePDF(selectedQuote.id, selectedQuote.customer_name, selectedQuote.quote_number)}
          >
            <Download className="mr-2 h-4 w-4" />
            Download PDF
          </Button>
          
          {selectedQuote.status === 'draft' && (
            <Button onClick={() => sendQuote(selectedQuote.id)}>
              <Send className="mr-2 h-4 w-4" />
              Send to Customer
            </Button>
          )}
        </div>
      </div>
    );
  }

  if (showCreateForm) {
    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <h2 className="text-2xl font-bold">Create New Quote</h2>
          <Button 
            variant="outline" 
            onClick={() => setShowCreateForm(false)}
          >
            Cancel
          </Button>
        </div>

        {/* Customer Information */}
        <Card>
          <CardHeader>
            <CardTitle>Customer Information</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Customer Email *</label>
                <input
                  type="email"
                  value={createForm.user_email}
                  onChange={(e) => setCreateForm({...createForm, user_email: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="customer@example.com"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Customer Name *</label>
                <input
                  type="text"
                  value={createForm.customer_name}
                  onChange={(e) => setCreateForm({...createForm, customer_name: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="John Smith"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-2">Postcode</label>
                <input
                  type="text"
                  value={createForm.customer_postcode}
                  onChange={(e) => setCreateForm({...createForm, customer_postcode: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  placeholder="BS1 1AA"
                />
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Windows */}
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>Windows ({createForm.windows.length})</span>
              <Button onClick={addWindow} size="sm">
                <Plus className="mr-2 h-4 w-4" />
                Add Window
              </Button>
            </CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-6">
              {createForm.windows.map((window, index) => (
                <div key={index} className="border rounded-lg p-4">
                  <div className="flex items-center justify-between mb-4">
                    <h4 className="font-semibold">Window {index + 1}</h4>
                    {createForm.windows.length > 1 && (
                      <Button 
                        variant="destructive" 
                        size="sm"
                        onClick={() => removeWindow(index)}
                      >
                        <Trash2 className="h-4 w-4" />
                      </Button>
                    )}
                  </div>
                  
                  <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                    {/* Basic Info */}
                    <div>
                      <label className="block text-sm font-medium mb-2">Label</label>
                      <input
                        type="text"
                        value={window.label}
                        onChange={(e) => updateWindow(index, 'label', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Width</label>
                      <input
                        type="number"
                        value={window.width}
                        onChange={(e) => updateWindow(index, 'width', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Height</label>
                      <input
                        type="number"
                        value={window.height}
                        onChange={(e) => updateWindow(index, 'height', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      />
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Unit</label>
                      <select
                        value={window.unit}
                        onChange={(e) => updateWindow(index, 'unit', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        <option value="mm">mm</option>
                        <option value="cm">cm</option>
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Window Type</label>
                      <select
                        value={window.type}
                        onChange={(e) => updateWindow(index, 'type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        {windowTypes.map(type => (
                          <option key={type.value} value={type.value}>
                            {type.label}
                          </option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Shutter Colour</label>
                      <select
                        value={window.shutter_colour}
                        onChange={(e) => updateWindow(index, 'shutter_colour', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        {shutterColours.map(colour => (
                          <option key={colour} value={colour}>{colour}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Louvre Size</label>
                      <select
                        value={window.louvre_size}
                        onChange={(e) => updateWindow(index, 'louvre_size', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        {louvreSizes.map(size => (
                          <option key={size} value={size}>{size}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Control Type</label>
                      <select
                        value={window.control_type}
                        onChange={(e) => updateWindow(index, 'control_type', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        {controlTypes.map(type => (
                          <option key={type} value={type}>{type}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Hinge Colour</label>
                      <select
                        value={window.hinge_colour}
                        onChange={(e) => updateWindow(index, 'hinge_colour', e.target.value)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                      >
                        {hingeColours.map(colour => (
                          <option key={colour} value={colour}>{colour}</option>
                        ))}
                      </select>
                    </div>
                    
                    <div>
                      <label className="block text-sm font-medium mb-2">Individual Discount (%)</label>
                      <input
                        type="number"
                        value={window.discount_percentage}
                        onChange={(e) => updateWindow(index, 'discount_percentage', parseFloat(e.target.value) || 0)}
                        className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        min="0"
                        max="100"
                      />
                    </div>
                  </div>
                  
                  {/* Window Cost Display */}
                  <div className="mt-4 p-3 bg-gray-50 rounded">
                    <div className="flex justify-between items-center">
                      <span>Window Cost: £{calculateWindowCost(window).cost}</span>
                      <span>Area: {calculateWindowCost(window).area}m²</span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          </CardContent>
        </Card>

        {/* Quote Details */}
        <Card>
          <CardHeader>
            <CardTitle>Quote Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium mb-2">Overall Discount (%)</label>
                <input
                  type="number"
                  value={createForm.overall_discount_percentage}
                  onChange={(e) => setCreateForm({...createForm, overall_discount_percentage: parseFloat(e.target.value) || 0})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                  min="0"
                  max="100"
                />
              </div>
              
              <div>
                <label className="block text-sm font-medium mb-2">Valid Until</label>
                <input
                  type="date"
                  value={createForm.valid_until}
                  onChange={(e) => setCreateForm({...createForm, valid_until: e.target.value})}
                  className="w-full px-3 py-2 border border-gray-300 rounded-md"
                />
              </div>
            </div>
            
            <div className="mt-4">
              <label className="block text-sm font-medium mb-2">Notes</label>
              <textarea
                value={createForm.notes}
                onChange={(e) => setCreateForm({...createForm, notes: e.target.value})}
                className="w-full px-3 py-2 border border-gray-300 rounded-md"
                rows="3"
                placeholder="Add any additional notes for the customer..."
              />
            </div>
          </CardContent>
        </Card>

        {/* Quote Summary */}
        <Card>
          <CardHeader>
            <CardTitle>Quote Summary</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="space-y-2">
              <div className="flex justify-between">
                <span>Windows:</span>
                <span>{createForm.windows.length}</span>
              </div>
              <div className="flex justify-between">
                <span>Total Area:</span>
                <span>{totals.totalArea}m²</span>
              </div>
              <div className="flex justify-between">
                <span>Subtotal:</span>
                <span>£{totals.subtotal}</span>
              </div>
              {createForm.overall_discount_percentage > 0 && (
                <div className="flex justify-between text-red-600">
                  <span>Overall Discount ({createForm.overall_discount_percentage}%):</span>
                  <span>-£{totals.overallDiscountAmount}</span>
                </div>
              )}
              <div className="flex justify-between text-lg font-bold border-t pt-2">
                <span>Total:</span>
                <span>£{totals.total}</span>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Actions */}
        <div className="flex gap-4">
          <Button 
            onClick={createQuote} 
            disabled={loading}
            style={{backgroundColor: '#8CB89F'}}
            className="hover:opacity-90"
          >
            {loading ? 'Creating...' : 'Create Quote'}
          </Button>
          <Button 
            variant="outline" 
            onClick={() => setShowCreateForm(false)}
          >
            Cancel
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-2xl font-bold">Quote Management</h2>
        <Button 
          onClick={() => setShowCreateForm(true)}
          style={{backgroundColor: '#8CB89F'}}
          className="hover:opacity-90"
        >
          <Plus className="mr-2 h-4 w-4" />
          Create Quote
        </Button>
      </div>

      {loading && <p>Loading quotes...</p>}

      <div className="space-y-4">
        {quotes.map((quote) => (
          <Card key={quote.id}>
            <CardContent className="p-6">
              <div className="flex items-center justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-4 mb-2">
                    <h3 className="text-lg font-semibold">Quote #{quote.quote_number}</h3>
                    <Badge className={getStatusBadgeColor(quote.status)}>
                      {quote.status}
                    </Badge>
                  </div>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4 text-sm text-gray-600">
                    <div>
                      <User className="inline h-4 w-4 mr-1" />
                      {quote.customer_name}
                    </div>
                    <div>
                      <MapPin className="inline h-4 w-4 mr-1" />
                      {quote.customer_postcode}
                    </div>
                    <div>
                      <Package className="inline h-4 w-4 mr-1" />
                      {quote.windows_count} windows
                    </div>
                    <div>
                      <DollarSign className="inline h-4 w-4 mr-1" />
                      £{quote.total_cost}
                    </div>
                  </div>
                  <div className="mt-2 text-sm text-gray-500">
                    Created: {new Date(quote.created_at).toLocaleDateString()} | 
                    Valid until: {new Date(quote.valid_until).toLocaleDateString()}
                  </div>
                </div>
                
                <div className="flex gap-2">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => viewQuoteDetails(quote.id)}
                  >
                    <Eye className="h-4 w-4" />
                  </Button>
                  
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => downloadQuotePDF(quote.id, quote.customer_name, quote.quote_number)}
                    title="Download PDF"
                  >
                    <Download className="h-4 w-4" />
                  </Button>
                  
                  {quote.status === 'draft' && (
                    <Button
                      size="sm"
                      onClick={() => sendQuote(quote.id)}
                      style={{backgroundColor: '#8CB89F'}}
                      className="hover:opacity-90"
                    >
                      <Send className="h-4 w-4" />
                    </Button>
                  )}
                </div>
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {quotes.length === 0 && !loading && (
        <Card>
          <CardContent className="p-12 text-center">
            <Calculator className="h-12 w-12 mx-auto mb-4 text-gray-400" />
            <h3 className="text-lg font-semibold mb-2">No quotes yet</h3>
            <p className="text-gray-600 mb-4">Create your first quote to get started</p>
            <Button 
              onClick={() => setShowCreateForm(true)}
              style={{backgroundColor: '#8CB89F'}}
              className="hover:opacity-90"
            >
              <Plus className="mr-2 h-4 w-4" />
              Create Quote
            </Button>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default QuoteManagement;